import { useCallback, useEffect, useRef, useState } from 'react'

import { useLiveHelperWebSocketUrl } from '@liveHelper/hooks/useLiveHelperWebSocketUrl'
import { SocketContext } from '@oneToOne/shared/contexts/SocketContext'

import { logDebug } from '@core/logging'
import { Socket } from '@core/phoenix/core'

/** Provides the socket in the context. */
export const SocketProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { endpoint, urlParams, shouldConnect } = useLiveHelperWebSocketUrl()
  const [socket, setSocket] = useState<Socket | undefined>(undefined)
  const [dynamicUrlParams, setDynamicUrlParams] =
    useState<Record<string, string>>(urlParams)

  const socketRef = useRef<Socket | undefined>(undefined)

  const createSocket = useCallback(
    (params = urlParams) => {
      setDynamicUrlParams(params)

      if (socket) {
        socket.disconnect()
      }

      if (endpoint && shouldConnect) {
        const newSocket = new Socket(endpoint, { params })
        newSocket.connect()
        newSocket.onError((err) => {
          logDebug(`Socket ${endpoint} error: ${err}`)
        })
        newSocket.onClose((evt) => {
          logDebug(`Socket ${endpoint} closed: ${evt}`)
        })
        setSocket(newSocket)
        socketRef.current = newSocket
      }
    },

    // Cannot have socket in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, shouldConnect, urlParams],
  )

  useEffect(() => {
    createSocket()
    return () => {
      socketRef.current?.disconnect()
    }
  }, [createSocket])

  // Needs to pass in socket from useState for rerendering
  return (
    <SocketContext.Provider
      value={{
        socket,
        recreateSocket: createSocket,
        urlParams: dynamicUrlParams,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
